package uk.co.nickthecoder.kyd.json

import uk.co.nickthecoder.glok.scene.Color
import uk.co.nickthecoder.kojak.JsonArray
import uk.co.nickthecoder.kojak.JsonObject
import uk.co.nickthecoder.kojak.JsonString
import uk.co.nickthecoder.kyd.Resource
import uk.co.nickthecoder.kyd.util.Vector2

/**
 * Looks for a `name` attribute, and throws an exception when now found.
 *
 * @param parent only used as part of the error message
 */
fun JsonObject.getName(parent: Resource?): String {
    return (get("name") as? JsonString)?.string
        ?: throw Exception("${parent?.path() ?: "/"} : Attribute `name` not found")
}

/**
 * [JsonObject.getString] takes a `default value`, when that attribute isn't present.
 * However, sometimes we want to throw an exception when the attribute is missing
 * (because there is no suitable default-value).
 *
 * @param resource A debugging aid for extra information in the Exception's message
 * @param name the name of the attribute
 * @throws Exception if attribute [name] is not found.
 */
fun JsonObject.getRequiredString(resource: Resource, name: String): String {
    return (get(name) as? JsonString)?.string ?: throw Exception("${resource.path()} : Attribute `$name` not found")
}

/**
 * If an attribute has a common default value, you may not want to include it
 * in the Json output. (to save space and speed).
 *
 * Calls [JsonObject.set] only if `value != defaultValue`.
 * When parsing, use [JsonObject.getString] with the same [defaultValue].
 */
fun JsonObject.addSkippingDefault(name: String, value: String, defaultValue: String) {
    if (value != defaultValue) {
        add(name, value)
    }
}

// The same as above, but for Ints.
fun JsonObject.addSkippingDefault(name: String, value: Int, defaultValue: Int) {
    if (value != defaultValue) {
        add(name, value)
    }
}

// The same as above, but for Float.
fun JsonObject.addSkippingDefault(name: String, value: Float, defaultValue: Float) {
    if (value != defaultValue) {
        add(name, value)
    }
}

// The same as above, but for Boolean.
fun JsonObject.addSkippingDefault(name: String, value: Boolean, defaultValue: Boolean) {
    if (value != defaultValue) {
        add(name, value)
    }
}

// The same as above, but for Color.
fun JsonObject.addSkippingDefault(name: String, value: Color, defaultValue: Color) {
    if (value != defaultValue) {
        add(name, value)
    }
}
/**
 * A convenience method to get all [JsonObject] within an attribute
 * which is assumed to be a [JsonArray].
 *
 * @param name the attribute's name, whose value is assumed to be a [JsonArray],
 *             where each item in the array is a [JsonObject].
 * @return a list of [JsonObject]s, or an empty-list if the attribute is not found
 *         (or contains no [JsonObject]s).
 */
fun JsonObject.getObjects(name: String): List<JsonObject> {
    val jArray = get(name) as? JsonArray ?: return emptyList()
    return jArray.values().filterIsInstance<JsonObject>()
}

/**
 * A convenience method to get all [JsonString] within an attribute
 * which is assumed to be a [JsonArray].
 *
 * @param name the attribute's name, whose value is assumed to be a [JsonArray],
 *             where each item in the array is a [JsonObject].
 * @return a list of [JsonObject]s, or an empty-list if the attribute is not found
 *         (or contains no [JsonObject]s).
 */
fun JsonObject.getStrings(name: String): List<JsonString> {
    val jArray = get(name) as? JsonArray ?: return emptyList()
    return jArray.values().filterIsInstance<JsonString>()
}

fun JsonObject.getVector2s(name: String): List<Vector2> {
    val jArray = get(name) as? JsonArray ?: return emptyList()
    return jArray.values().filterIsInstance<JsonObject>().map {
        val x = it.getFloat("x", 0f)
        val y = it.getFloat("y", 0f)
        Vector2(x, y)
    }
}



fun JsonObject.getColor(name: String, defaultValue: Color): Color {

    return try {
        val str = get(name)?.asString()
        if (str == null) {
            defaultValue
        } else {
            Color[str]
        }

    } catch (e: Exception) {
        defaultValue
    }
}

fun JsonObject.add(name: String, color: Color) {
    add(name, color.toString())
}
